import { Link } from 'react-router-dom';
import { Camera, Sparkles, MapPin, Navigation, Apple, Mail, Phone } from 'lucide-react';
import { contact, whatsappLink } from '../data/site';
import { trackWhatsAppClick } from '../lib/analytics';
import { WhatsAppIcon } from '../components/icons';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const values = [
  {
    Icon: Camera,
    title: 'An editorial eye',
    body: 'We turn a vacation into a high-fashion editorial - composed light, considered styling and direction that makes everyone look their best.',
  },
  {
    Icon: Sparkles,
    title: 'Effortless, pick-up to print',
    body: 'Pick-up and drop-off, posing guidance, drinks and bites, fast delivery. You simply show up; we take care of every detail.',
  },
  {
    Icon: MapPin,
    title: 'Santorini insiders',
    body: 'Hidden gems, caldera and volcano-view spots and the best light of the day - we know exactly where and when to shoot.',
  },
];

export function AboutPage() {
  useDocumentTitle('About Flying Muse | Santorini Photography Studio');

  return (
    <div>
      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/img/casual.webp" alt="" aria-hidden="true" width={1600} height={1175} className="h-full w-full object-cover object-[50%_35%]" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/65 to-navy-deep/35" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/70 via-transparent to-navy-deep/30" />
        </div>
        <div className="container-content section-pad relative z-10 pb-16 pt-32 sm:pb-20 sm:pt-40">
          <p className="eyebrow text-crimson-soft">Our Story</p>
          <h1 className="mt-4 max-w-2xl font-display text-[clamp(2.5rem,6vw,4.5rem)] font-medium leading-[1.04] text-white">
            Where the dress takes flight
          </h1>
          <p className="mt-6 max-w-xl font-sans text-base font-light leading-relaxed text-white/85">
            Flying Muse is a high-end photography &amp; experience studio in Fira, Santorini -
            famous for the Flying Dress Experience, where flowing gowns catch the Aegean wind above
            the caldera.
          </p>
        </div>
      </header>

      {/* Visit us */}
      <section className="bg-cream py-20 sm:py-24">
        <div className="container-content section-pad">
          <div className="overflow-hidden rounded-2xl bg-white shadow-card md:grid md:grid-cols-2">
            <div className="p-8 sm:p-10 lg:p-12">
              <p className="eyebrow">Find Us</p>
              <h2 className="mt-4 font-display text-[clamp(1.8rem,4vw,2.5rem)] font-medium text-charcoal">
                Visit the studio
              </h2>
              <p className="mt-5 font-sans text-[0.98rem] font-light leading-relaxed text-stone">
                We&rsquo;re based in {contact.location}. Tap below for directions, or message us and
                we&rsquo;ll arrange pick-up and drop-off for your session.
              </p>

              <div className="mt-7 flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-crimson" strokeWidth={1.7} />
                <span className="font-sans text-sm font-light text-charcoal/80">
                  {contact.location}
                </span>
              </div>

              <div className="mt-7 flex flex-wrap gap-4">
                <a
                  href={contact.googleMaps}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  <Navigation className="h-4 w-4" strokeWidth={2} />
                  Google Maps
                </a>
                <a
                  href={contact.appleMaps}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-dark"
                >
                  <Apple className="h-4 w-4" />
                  Apple Maps
                </a>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick('about_studio')}
                  className="btn btn-outline-dark"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>

              <div className="mt-8 space-y-3 border-t border-charcoal/10 pt-7">
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-3 font-sans text-sm font-light text-charcoal/80 transition-colors hover:text-crimson"
                >
                  <Mail className="h-4 w-4 text-crimson" strokeWidth={1.7} />
                  {contact.email}
                </a>
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center gap-3 font-sans text-sm font-light text-charcoal/80 transition-colors hover:text-crimson"
                >
                  <Phone className="h-4 w-4 text-crimson" strokeWidth={1.7} />
                  {contact.phone}
                </a>
              </div>
            </div>

            <a
              href={contact.googleMaps}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open Flying Muse on Google Maps"
              className="group relative block min-h-[18rem] overflow-hidden"
            >
              <img
                src="/img/wedding.webp"
                alt="Santorini caldera"
                loading="lazy"
                width={1100}
                height={576}
                className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-navy-deep/30 transition-colors group-hover:bg-navy-deep/20" />
              <span className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 font-sans text-[0.65rem] font-semibold uppercase tracking-wide2 text-charcoal shadow-card">
                <MapPin className="h-4 w-4 text-crimson" strokeWidth={2} />
                Open in Google Maps
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-cream py-20 sm:py-24">
        <div className="container-content section-pad grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
          <div className="order-2 lg:order-1">
            <p className="eyebrow">The Studio</p>
            <h2 className="mt-4 font-display text-[clamp(1.9rem,4vw,2.75rem)] font-medium text-charcoal">
              A vacation, turned into an editorial
            </h2>
            <div className="mt-6 space-y-5 font-sans text-[0.98rem] font-light leading-relaxed text-stone">
              <p>
                Flying Muse was born from a love of Santorini and a belief that your memories of it
                should feel as breathtaking as the island itself. We are a small, dedicated team of
                photographers and stylists based in Fira, working in the most beautiful corners of
                the caldera.
              </p>
              <p>
                Our signature is the Flying Dress Experience - long, flowing gowns that catch the
                Aegean wind for striking, cinematic portraits. Beyond it, we create casual lifestyle
                sessions, private island photoshooting tours and tailor-made love stories for
                proposals, honeymoons and anniversaries.
              </p>
              <p>
                Every session is private and unhurried. We guide your posing, share the island&rsquo;s
                hidden gems and deliver high-resolution raw photos within a day and professionally
                edited images within three.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/experiences" className="btn btn-primary">
                Explore Experiences
              </Link>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('about_footer')}
                className="btn btn-outline-dark"
              >
                Say Hello
              </a>
            </div>
          </div>

          <div className="order-1 overflow-hidden rounded-2xl shadow-card lg:order-2">
            <img
              src="/img/studio.webp"
              alt="Inside the Flying Muse photography studio in Fira, Santorini"
              loading="lazy"
              width={1600}
              height={900}
              className="aspect-[10/9] h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-cream-soft py-20 sm:py-24">
        <div className="container-content section-pad">
          <div className="text-center">
            <p className="eyebrow eyebrow--center">What Sets Us Apart</p>
            <h2 className="mt-4 font-display text-[clamp(2rem,4.5vw,3rem)] font-medium text-charcoal">
              Crafted, calm and unmistakably Santorini
            </h2>
          </div>
          <div className="mt-14 grid gap-10 sm:grid-cols-3">
            {values.map(({ Icon, title, body }) => (
              <div key={title} className="text-center">
                <Icon className="mx-auto h-9 w-9 text-crimson" strokeWidth={1.4} />
                <h3 className="mt-5 font-display text-xl font-medium text-charcoal">{title}</h3>
                <p className="mx-auto mt-3 max-w-xs font-sans text-sm font-light leading-relaxed text-stone">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
